import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import DocumentUploader from '../components/DocumentUploader';

type Vendor = {
  id: string;
  name: string;
  uei: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  payment_terms: string | null;
  tier: 'primary'|'secondary'|'backup';
  notes: string | null;
  item_count: number;
  active_baba_certs: number;
  total_baba_certs: number;
  // Compliance (Phase 2)
  sam_gov_status: 'active' | 'inactive' | 'debarred' | 'excluded' | 'unchecked' | null;
  sam_gov_checked_at: string | null;
  dfw_cert_on_file: boolean;
  whistleblower_ack_at: string | null;
  e_verify_enrolled_at: string | null;
  title_vi_clause_ack_at: string | null;
  mbe_status: 'certified' | 'self_attested' | 'no' | null;
  wbe_status: 'certified' | 'self_attested' | 'no' | null;
  veteran_status: 'certified' | 'self_attested' | 'no' | null;
  sba_8a_status: 'certified' | 'self_attested' | 'no' | null;
  active_insurance_certs: number;
  insurance_expiring_soon: number;
};

type InsuranceCert = {
  id: string;
  cert_kind: 'cgl' | 'auto' | 'wc' | 'professional' | 'builders_risk'
           | 'pollution' | 'crime' | 'cyber' | 'umbrella' | 'other';
  carrier: string | null;
  policy_number: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  per_occurrence_limit_usd: string | number | null;
  aggregate_limit_usd: string | number | null;
  state_additional_insured: boolean;
  status: 'active' | 'expired' | 'pending' | 'cancelled';
  notes: string | null;
  currency_flag: 'no_expiry' | 'current' | 'expiring_soon' | 'expired';
};

type VendorItem = {
  id: string;
  is_primary: boolean;
  current_price_id: string;
  line_item: string;
  category: string | null;
  unit: string | null;
  current_unit_cost_usd: string | null;
};

type BabaCert = {
  id: string;
  cert_type: string;
  cert_reference: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  status: string;
  document_filename: string | null;
};

type VendorDetail = {
  vendor: Vendor;
  items: VendorItem[];
  baba_certs: BabaCert[];
  insurance_certs: InsuranceCert[];
};

export default function VendorsPage() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ['bead-vendors'],
    queryFn: () => api.get('/bead/vendors'),
  });
  const { data: detail } = useQuery<VendorDetail>({
    queryKey: ['bead-vendor', activeId],
    queryFn: () => activeId ? api.get(`/bead/vendors/${activeId}`) : Promise.reject(),
    enabled: !!activeId,
  });
  const { data: costItems = [] } = useQuery<any[]>({
    queryKey: ['bead-costs'],
    queryFn: () => api.get('/bead/costs'),
  });

  const createMut = useMutation({
    mutationFn: (body: Partial<Vendor>) => api.post('/bead/vendors', body),
    onSuccess: (v: Vendor) => {
      qc.invalidateQueries({ queryKey: ['bead-vendors'] });
      setShowAdd(false);
      setActiveId(v.id);
    },
  });
  const assignItemMut = useMutation({
    mutationFn: (body: { vendorId: string; current_price_id: string; is_primary: boolean }) =>
      api.post(`/bead/vendors/${body.vendorId}/items`, { current_price_id: body.current_price_id, is_primary: body.is_primary }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bead-vendor', activeId] }),
  });
  const unassignItemMut = useMutation({
    mutationFn: (id: string) => api.del(`/bead/vendor-items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bead-vendor', activeId] }),
  });

  return (
    <div style={{ padding: 20, fontSize: 13 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>Vendors</h1>
        <button className="btn btn--primary" onClick={() => setShowAdd(true)}>+ Add vendor</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 20 }}>
        {/* Vendor list */}
        <div>
          {vendors.length === 0 && (
            <div style={{ padding: 20, color: '#64748b', background: '#f1f5f9', borderRadius: 6 }}>
              No vendors yet. Add your first.
            </div>
          )}
          {vendors.map((v) => (
            <div
              key={v.id}
              onClick={() => setActiveId(v.id)}
              style={{
                padding: 12, marginBottom: 6, borderRadius: 6, cursor: 'pointer',
                background: activeId === v.id ? '#dbeafe' : '#fff',
                border: `1px solid ${activeId === v.id ? '#3b82f6' : 'var(--border)'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{v.name}</div>
                <span style={{
                  fontSize: 10, padding: '2px 6px', borderRadius: 3,
                  background: v.tier === 'primary' ? '#dcfce7' : v.tier === 'secondary' ? '#fef3c7' : '#f1f5f9',
                  color:      v.tier === 'primary' ? '#047857' : v.tier === 'secondary' ? '#92400e' : '#64748b',
                }}>{v.tier}</span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                {v.contact_name ?? 'No contact'} {v.contact_email ? `· ${v.contact_email}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#64748b', marginTop: 4, flexWrap: 'wrap' }}>
                <span>{v.item_count} items</span>
                <span>· {v.active_baba_certs} BABA</span>
                <span>· {v.active_insurance_certs} ins.</span>
                {v.insurance_expiring_soon > 0 && (
                  <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                    ⚠ {v.insurance_expiring_soon} expiring &lt; 30d
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                {v.sam_gov_status && (
                  <span style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                    background: v.sam_gov_status === 'active' ? '#dcfce7'
                              : v.sam_gov_status === 'debarred' || v.sam_gov_status === 'excluded' ? '#fee2e2'
                              : '#f1f5f9',
                    color: v.sam_gov_status === 'active' ? '#047857'
                         : v.sam_gov_status === 'debarred' || v.sam_gov_status === 'excluded' ? '#b91c1c'
                         : '#64748b',
                  }}>SAM: {v.sam_gov_status}</span>
                )}
                {v.mbe_status === 'certified' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#ede9fe', color: '#5b21b6', fontWeight: 600 }}>MBE</span>}
                {v.wbe_status === 'certified' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#fce7f3', color: '#9d174d', fontWeight: 600 }}>WBE</span>}
                {v.veteran_status === 'certified' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>Veteran</span>}
                {v.sba_8a_status === 'certified' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>8(a)</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Vendor detail */}
        <div>
          {!detail && (
            <div style={{ padding: 40, color: '#64748b', background: '#f8fafc', borderRadius: 6, textAlign: 'center' }}>
              Select a vendor to see assigned items + BABA certs.
            </div>
          )}
          {detail && <VendorDetailView
            detail={detail}
            costItems={costItems}
            onAssign={(priceId, isPrimary) => assignItemMut.mutate({ vendorId: detail.vendor.id, current_price_id: priceId, is_primary: isPrimary })}
            onUnassign={(id) => unassignItemMut.mutate(id)}
          />}
        </div>
      </div>

      {showAdd && (
        <AddVendorModal
          onCancel={() => setShowAdd(false)}
          onSubmit={(body) => createMut.mutate(body)}
          busy={createMut.isPending}
        />
      )}
    </div>
  );
}

function VendorDetailView({ detail, costItems, onAssign, onUnassign }: {
  detail: VendorDetail;
  costItems: any[];
  onAssign: (priceId: string, isPrimary: boolean) => void;
  onUnassign: (id: string) => void;
}) {
  const { vendor, items, baba_certs } = detail;
  const [assignSel, setAssignSel] = useState('');
  const assignedIds = new Set(items.map((i) => i.current_price_id));
  const availableItems = costItems.filter((ci) => !assignedIds.has(ci.id));

  return (
    <div>
      <div style={{ background: '#0b1220', color: '#fff', padding: 16, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>{vendor.name}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
          {vendor.uei && <span>UEI: {vendor.uei} · </span>}
          {vendor.payment_terms ?? 'no terms set'}
        </div>
        {(vendor.contact_name || vendor.contact_email || vendor.contact_phone) && (
          <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 8 }}>
            {vendor.contact_name && <span>{vendor.contact_name}</span>}
            {vendor.contact_email && <span> · <a style={{ color: '#7dd3fc' }} href={`mailto:${vendor.contact_email}`}>{vendor.contact_email}</a></span>}
            {vendor.contact_phone && <span> · {vendor.contact_phone}</span>}
          </div>
        )}
        {vendor.website && (
          <div style={{ fontSize: 12, marginTop: 4 }}>
            <a style={{ color: '#7dd3fc' }} href={vendor.website} target="_blank" rel="noreferrer">{vendor.website}</a>
          </div>
        )}
      </div>

      {/* Items assigned */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', margin: 0 }}>
            Items supplied ({items.length})
          </h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={assignSel} onChange={(e) => setAssignSel(e.target.value)} style={{ fontSize: 12, padding: '4px 6px' }}>
              <option value="">+ Assign item…</option>
              {availableItems.map((ci: any) => (
                <option key={ci.id} value={ci.id}>{ci.line_item}</option>
              ))}
            </select>
            <button
              className="btn"
              disabled={!assignSel}
              onClick={() => { onAssign(assignSel, false); setAssignSel(''); }}
              style={{ fontSize: 12 }}
            >Add</button>
          </div>
        </div>
        {items.length === 0 && <div style={{ color: '#94a3b8', padding: 12 }}>No items assigned yet.</div>}
        {items.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Line item</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', width: 60 }}>Unit</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', width: 100 }}>Current $</th>
                <th style={{ padding: '6px 8px', width: 70 }}>Primary?</th>
                <th style={{ padding: '6px 8px', width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <div>{it.line_item}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{it.category}</div>
                  </td>
                  <td style={{ padding: '6px 8px', color: '#64748b' }}>{it.unit}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {it.current_unit_cost_usd ? `$${Number(it.current_unit_cost_usd).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    {it.is_primary && <span style={{ fontSize: 11, color: '#047857' }}>✓</span>}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <button onClick={() => onUnassign(it.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* BABA certs */}
      <div>
        <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', marginBottom: 6 }}>
          BABA compliance ({baba_certs.length})
        </h3>
        {baba_certs.length === 0 && (
          <div style={{ color: '#94a3b8', padding: 12, background: '#fef3c7', borderRadius: 6 }}>
            No BABA certs on file for this vendor. Required for BEAD reimbursement.
          </div>
        )}
        {baba_certs.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Cert type</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Ref</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>File</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Expires</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {baba_certs.map((c) => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px' }}>{c.cert_type}</td>
                  <td style={{ padding: '6px 8px' }}>{c.cert_reference ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{c.document_filename ?? <span style={{ color: '#dc2626' }}>missing</span>}</td>
                  <td style={{ padding: '6px 8px' }}>{c.expiry_date ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3,
                                   background: c.status === 'active' ? '#dcfce7' : '#fee2e2',
                                   color:      c.status === 'active' ? '#047857' : '#b91c1c' }}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
          File uploads (PDF cert) — coming in next patch. Metadata can be entered for now.
        </div>
      </div>

      {/* Insurance certs */}
      <InsuranceCertsSection vendorId={vendor.id} certs={detail.insurance_certs ?? []} />

      {/* Compliance status */}
      <ComplianceStatusSection vendor={vendor} />
    </div>
  );
}

// --------------------------------------------------------------------
// Insurance Certs Section
// --------------------------------------------------------------------

const CERT_LABEL: Record<string, string> = {
  cgl: 'CGL', auto: 'Auto', wc: 'Workers Comp', professional: 'Professional',
  builders_risk: 'Builders Risk', pollution: 'Pollution', crime: 'Crime',
  cyber: 'Cyber', umbrella: 'Umbrella', other: 'Other',
};

const CURRENCY_BADGE_INS: Record<string, { bg: string; fg: string; label: string }> = {
  current:        { bg: '#d1fae5', fg: '#065f46', label: 'Current' },
  expiring_soon:  { bg: '#fef3c7', fg: '#92400e', label: '< 30 days' },
  expired:        { bg: '#fee2e2', fg: '#991b1b', label: 'Expired' },
  no_expiry:      { bg: '#e5e7eb', fg: '#6b7280', label: 'No expiry' },
};

const fmtMoney = (n: any) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${Math.round(v)}`;
};

function InsuranceCertsSection({ vendorId, certs }: { vendorId: string; certs: InsuranceCert[] }) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const createMut = useMutation({
    mutationFn: (body: any) => api.post(`/bead/vendors/${vendorId}/insurance-certs`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bead-vendor', vendorId] });
      qc.invalidateQueries({ queryKey: ['bead-vendors'] });
      setShowAdd(false);
    },
  });
  const delMut = useMutation({
    mutationFn: (certId: string) => api.del(`/bead/vendors/${vendorId}/insurance-certs/${certId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bead-vendor', vendorId] });
      qc.invalidateQueries({ queryKey: ['bead-vendors'] });
    },
  });

  const expiringSoon = certs.filter(c => c.currency_flag === 'expiring_soon').length;
  const expired = certs.filter(c => c.currency_flag === 'expired').length;

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', margin: 0 }}>
          Insurance certificates ({certs.length})
          {expiringSoon > 0 && <span style={{ marginLeft: 8, color: '#92400e', fontSize: 11 }}>· {expiringSoon} expiring &lt; 30d</span>}
          {expired > 0 && <span style={{ marginLeft: 8, color: '#991b1b', fontSize: 11 }}>· {expired} expired</span>}
        </h3>
        <button className="btn" style={{ fontSize: 12 }} onClick={() => setShowAdd(s => !s)}>
          {showAdd ? '× Cancel' : '+ Add cert'}
        </button>
      </div>

      {showAdd && <AddInsuranceCertForm onSubmit={(b) => createMut.mutate(b)} busy={createMut.isPending} />}

      {certs.length === 0 ? (
        <div style={{ color: '#94a3b8', padding: 12, background: '#fef3c7', borderRadius: 6 }}>
          No insurance certs on file. State sub-grant typically requires CGL, Auto, and Workers Comp at minimum.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Type</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Carrier</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Policy #</th>
              <th style={{ textAlign: 'right', padding: '6px 8px' }}>Per occ.</th>
              <th style={{ textAlign: 'right', padding: '6px 8px' }}>Aggregate</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Expires</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Currency</th>
              <th style={{ padding: '6px 8px', width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {certs.map(c => {
              const cb = CURRENCY_BADGE_INS[c.currency_flag] ?? CURRENCY_BADGE_INS.no_expiry;
              return (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>{CERT_LABEL[c.cert_kind] ?? c.cert_kind}</td>
                  <td style={{ padding: '6px 8px', color: '#64748b' }}>{c.carrier ?? '—'}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{c.policy_number ?? '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(c.per_occurrence_limit_usd)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(c.aggregate_limit_usd)}</td>
                  <td style={{ padding: '6px 8px', color: '#64748b', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{c.expiration_date ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                                   background: cb.bg, color: cb.fg }}>{cb.label}</span>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <button onClick={() => { if (confirm('Delete this cert?')) delMut.mutate(c.id); }}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AddInsuranceCertForm({ onSubmit, busy }: { onSubmit: (b: any) => void; busy: boolean }) {
  const [f, setF] = useState<any>({ cert_kind: 'cgl', state_additional_insured: true });
  const inp: React.CSSProperties = {
    width: '100%', padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4,
  };
  return (
    <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Type</div>
          <select style={inp} value={f.cert_kind} onChange={e => setF({ ...f, cert_kind: e.target.value })}>
            {Object.entries(CERT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Carrier</div>
          <input style={inp} value={f.carrier ?? ''} onChange={e => setF({ ...f, carrier: e.target.value })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Policy #</div>
          <input style={inp} value={f.policy_number ?? ''} onChange={e => setF({ ...f, policy_number: e.target.value })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Expires</div>
          <input type="date" style={inp} value={f.expiration_date ?? ''} onChange={e => setF({ ...f, expiration_date: e.target.value })} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Per occurrence ($)</div>
          <input type="number" style={inp} value={f.per_occurrence_limit_usd ?? ''} onChange={e => setF({ ...f, per_occurrence_limit_usd: e.target.value })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Aggregate ($)</div>
          <input type="number" style={inp} value={f.aggregate_limit_usd ?? ''} onChange={e => setF({ ...f, aggregate_limit_usd: e.target.value })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>Effective</div>
          <input type="date" style={inp} value={f.effective_date ?? ''} onChange={e => setF({ ...f, effective_date: e.target.value })} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <label style={{ fontSize: 11, color: '#475569' }}>
            <input type="checkbox" checked={f.state_additional_insured} onChange={e => setF({ ...f, state_additional_insured: e.target.checked })} />
            {' '}State additional insured
          </label>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <DocumentUploader
          value={f.document_id ?? null}
          onChange={(id) => setF({ ...f, document_id: id })}
          docType="insurance_coi"
          label="Certificate of insurance (PDF)"
          hint="Upload the carrier-issued COI for this policy."
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn--primary" disabled={busy || !f.cert_kind} onClick={() => onSubmit(f)} style={{ fontSize: 12 }}>
          {busy ? 'Saving…' : 'Save cert'}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------
// Compliance Status Section
// --------------------------------------------------------------------

function ComplianceStatusSection({ vendor }: { vendor: Vendor }) {
  const qc = useQueryClient();
  const updateMut = useMutation({
    mutationFn: (body: any) => api.put(`/bead/vendors/${vendor.id}/compliance`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bead-vendor', vendor.id] });
      qc.invalidateQueries({ queryKey: ['bead-vendors'] });
    },
  });

  const samStub = () => updateMut.mutate({
    sam_gov_status: 'active',  // stub: real impl would call SAM.gov API
    sam_gov_checked_at: new Date().toISOString(),
  });

  const Row = ({ label, value, ok }: { label: string; value: string; ok: boolean }) => (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={{ padding: '6px 8px', color: '#64748b' }}>{label}</td>
      <td style={{ padding: '6px 8px' }}>{value}</td>
      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
        <span style={{
          fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
          background: ok ? '#dcfce7' : '#fee2e2',
          color: ok ? '#047857' : '#b91c1c',
        }}>{ok ? '✓ on file' : '✗ missing'}</span>
      </td>
    </tr>
  );

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', margin: 0 }}>
          Compliance status
        </h3>
        <button className="btn" style={{ fontSize: 12 }} onClick={samStub} disabled={updateMut.isPending}>
          {updateMut.isPending ? 'Checking…' : '🔍 Check SAM.gov'}
        </button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <tbody>
          <Row
            label="SAM.gov status (debarment check)"
            value={vendor.sam_gov_status ? `${vendor.sam_gov_status}${vendor.sam_gov_checked_at ? ` — checked ${vendor.sam_gov_checked_at.slice(0, 10)}` : ''}` : 'unchecked'}
            ok={vendor.sam_gov_status === 'active'}
          />
          <Row
            label="Drug-Free Workplace policy"
            value={vendor.dfw_cert_on_file ? 'Cert on file' : '—'}
            ok={vendor.dfw_cert_on_file}
          />
          <Row
            label="Whistleblower policy ack"
            value={vendor.whistleblower_ack_at ?? '—'}
            ok={!!vendor.whistleblower_ack_at}
          />
          <Row
            label="E-Verify enrollment"
            value={vendor.e_verify_enrolled_at ?? '—'}
            ok={!!vendor.e_verify_enrolled_at}
          />
          <Row
            label="Title VI subcontractor clause ack"
            value={vendor.title_vi_clause_ack_at ?? '—'}
            ok={!!vendor.title_vi_clause_ack_at}
          />
          <Row
            label="MBE certification"
            value={vendor.mbe_status ?? '—'}
            ok={vendor.mbe_status === 'certified' || vendor.mbe_status === 'self_attested'}
          />
          <Row
            label="WBE certification"
            value={vendor.wbe_status ?? '—'}
            ok={vendor.wbe_status === 'certified' || vendor.wbe_status === 'self_attested'}
          />
          <Row
            label="Veteran-owned status"
            value={vendor.veteran_status ?? '—'}
            ok={vendor.veteran_status === 'certified' || vendor.veteran_status === 'self_attested'}
          />
          <Row
            label="SBA 8(a) status"
            value={vendor.sba_8a_status ?? '—'}
            ok={vendor.sba_8a_status === 'certified' || vendor.sba_8a_status === 'self_attested'}
          />
        </tbody>
      </table>
    </div>
  );
}

function AddVendorModal({ onCancel, onSubmit, busy }: {
  onCancel: () => void;
  onSubmit: (body: any) => void;
  busy: boolean;
}) {
  const [form, setForm] = useState<any>({ tier: 'primary' });
  const F = (label: string, key: string, type = 'text') => (
    <label style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
      <div style={{ color: '#64748b', marginBottom: 2 }}>{label}</div>
      <input
        type={type}
        value={form[key] ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13 }}
      />
    </label>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: 20, borderRadius: 8, width: 480, maxHeight: '90vh', overflow: 'auto' }}>
        <h2 style={{ fontSize: 16, margin: 0, marginBottom: 12 }}>New vendor</h2>
        {F('Name', 'name')}
        {F('UEI (SAM.gov)', 'uei')}
        {F('Contact name', 'contact_name')}
        {F('Contact email', 'contact_email', 'email')}
        {F('Contact phone', 'contact_phone')}
        {F('Website', 'website', 'url')}
        {F('Address', 'address')}
        {F('Payment terms', 'payment_terms')}
        <label style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          <div style={{ color: '#64748b', marginBottom: 2 }}>Tier</div>
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} style={{ width: '100%', padding: '6px 8px' }}>
            <option value="primary">primary</option>
            <option value="secondary">secondary</option>
            <option value="backup">backup</option>
          </select>
        </label>
        {F('Notes', 'notes')}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn--primary" onClick={() => onSubmit(form)} disabled={busy || !form.name}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
